// How a language change EXECUTES, per playback architecture (CPACWEB-420).
//
// Two mechanisms exist, chosen by what the platform can be trusted to do:
//
//  - MSE providers (JW 'hlsjs' — Chrome/Edge/Firefox/Brave): the JS layer genuinely controls
//    rendition selection and switches are verifiable → instant IN-PLACE switch, delegated to
//    the AudioTrackReconciler.
//  - The NATIVE HLS provider (JW 'html5' — all iOS/iPadOS browsers, Safari playing natively):
//    playback runs on AVFoundation, where the audioTracks API is silently unreliable —
//    verified on-device 2026-07-04: commands accepted, events fired, enabled flags flipped,
//    audio unchanged; failure is sporadic and has NO JS-visible signature, so detection is
//    impossible and the only sound move is structural → RELOAD the source at the current
//    position and let the fresh session select.
//
// The reload routes the stream URL through buildStreamUrl(file, lang): a pass-through today,
// so this relies on the fresh session cooperating (already far better odds than toggling a
// possibly-locked session). Once Vualto's defLang parameter lands there, the reloaded
// manifest carries exactly ONE auto-selectable rendition and this same mechanism becomes
// fully deterministic with no change to this class.
//
// Plain TypeScript with injected dependencies — unit-tested with a fake player in
// audio-language-switcher.test.ts. CpacJwPlayerContainer wires it to JW.
//
// Architecture doc: doc/video-player-audio-architecture.md

import { AudioLang, audioDebug, buildStreamUrl } from "./audio-tracks.ts";
import { JwPlaylistItem } from "../../types/JwPlaylistItem.ts";

/** The player surface the switcher needs. */
export type SwitcherPlayer = {
  getProvider?: () => { name?: string } | undefined;
  getPosition: () => number;
  load: (playlist: JwPlaylistItem[]) => void;
  once: (event: string, handler: () => void) => void;
  seek: (seconds: number) => void;
  play: () => void;
};

/** The reconciler surface the switcher needs (avoids a hard class dependency). */
export type SwitcherReconciler = {
  sourceChanged: () => void;
  desiredLangChanged: () => void;
};

export type AudioLanguageSwitcherDeps = {
  getPlayer: () => SwitcherPlayer | null;
  /** The player's <video> element, for the audioTracks-presence fallback check. */
  getVideoElement: () => { audioTracks?: { length: number } } | null;
  /** The current JW playlist entry (title/description/category/image/file). */
  getPlaylistItem: () => JwPlaylistItem;
  /** The raw stream URL BEFORE the buildStreamUrl seam (the reload re-applies the seam). */
  getBaseFile: () => string | undefined;
  /** Live streams rejoin the edge; only VOD seeks back to the captured position. */
  isVod: () => boolean;
  reconciler: SwitcherReconciler;
  /** Container housekeeping after a source reload (e.g. reset track-membership snapshot). */
  onSourceReloaded?: () => void;
};

export type SwitchMechanism = "in-place" | "reload";

export class AudioLanguageSwitcher {
  constructor(private readonly deps: AudioLanguageSwitcherDeps) {}

  /**
   * Native = playback on AVFoundation. Primary signal is JW's provider name ('html5');
   * fallback is a populated audioTracks list on the media element (MSE providers don't
   * populate it).
   */
  isNativeHlsProvider(): boolean {
    const player = this.deps.getPlayer();
    const providerName = player && player.getProvider ? player.getProvider()?.name : undefined;
    if (providerName === "html5") return true;
    const videoEl = this.deps.getVideoElement();
    return !!(videoEl && videoEl.audioTracks && videoEl.audioTracks.length > 0);
  }

  /**
   * Execute a desired-language change. `playerInitialized` is false before the first
   * setup() — nothing to reload yet, so the reconciler simply presets the upcoming session.
   * Returns which mechanism ran (for logging and tests).
   */
  apply(lang: AudioLang, playerInitialized: boolean): SwitchMechanism {
    const player = this.deps.getPlayer();
    if (!player || !playerInitialized || !this.isNativeHlsProvider()) {
      this.deps.reconciler.desiredLangChanged();
      return "in-place";
    }
    const position = player.getPosition ? player.getPosition() : 0;
    const base = this.deps.getBaseFile();
    const item = this.deps.getPlaylistItem();
    console.info(
      `[CPACWEB-420] audio switch via source reload (native provider): lang=${lang}, pos=${Math.round(position)}s`,
    );
    this.deps.reconciler.sourceChanged();
    if (this.deps.onSourceReloaded) this.deps.onSourceReloaded();
    player.load([{ ...item, file: base ? buildStreamUrl(base, lang) : item.file }]);
    if (this.deps.isVod() && position > 0) {
      player.once("firstFrame", () => player.seek(position));
    }
    player.play();
    // Defense in depth: the reconciler presets/verifies the fresh session as usual.
    this.deps.reconciler.desiredLangChanged();
    audioDebug("language switch executed via reload", { lang, position });
    return "reload";
  }
}
